/* eslint-disable @next/next/no-img-element */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

interface McpFlowNode {
  name: string;
  icon?: string;
}

interface McpFlowAnimationProps {
  sources: McpFlowNode[];
  tools: McpFlowNode[];
  hubLabel?: string;
}

interface Beam {
  d: string;
}

function buildBeam(
  from: { x: number; y: number },
  to: { x: number; y: number },
) {
  const horizontal = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
  const c1 = horizontal
    ? `${(from.x + to.x) / 2} ${from.y}`
    : `${from.x} ${(from.y + to.y) / 2}`;
  const c2 = horizontal
    ? `${(from.x + to.x) / 2} ${to.y}`
    : `${to.x} ${(from.y + to.y) / 2}`;
  return { d: `M ${from.x} ${from.y} C ${c1}, ${c2}, ${to.x} ${to.y}` };
}

export default function McpFlowAnimation({
  sources,
  tools,
  hubLabel = "快客 MCP",
}: McpFlowAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hubRef = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const toolRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [inBeams, setInBeams] = useState<Beam[]>([]);
  const [outBeams, setOutBeams] = useState<Beam[]>([]);

  const isInView = useInView(containerRef, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  const measure = useCallback(() => {
    const container = containerRef.current;
    const hub = hubRef.current;
    if (!container || !hub) return;

    const cRect = container.getBoundingClientRect();
    const hubRect = hub.getBoundingClientRect();
    const hubCenter = {
      x: hubRect.left + hubRect.width / 2 - cRect.left,
      y: hubRect.top + hubRect.height / 2 - cRect.top,
    };

    const centerOf = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return {
        x: r.left + r.width / 2 - cRect.left,
        y: r.top + r.height / 2 - cRect.top,
      };
    };

    setSize({ width: cRect.width, height: cRect.height });
    setInBeams(
      sourceRefs.current
        .filter((el): el is HTMLDivElement => el !== null)
        .map((el) => buildBeam(centerOf(el), hubCenter)),
    );
    setOutBeams(
      toolRefs.current
        .filter((el): el is HTMLDivElement => el !== null)
        .map((el) => buildBeam(hubCenter, centerOf(el))),
    );
  }, []);

  useEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  const beams = [...inBeams, ...outBeams];

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col items-center justify-between gap-10 sm:flex-row sm:gap-6 max-w-4xl mx-auto"
    >
      {size.width > 0 && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${size.width} ${size.height}`}
          fill="none"
          aria-hidden="true"
        >
          {beams.map((beam, index) => (
            <path
              key={`track-${index}`}
              d={beam.d}
              stroke="#ffffff"
              strokeOpacity={1.2}
              strokeWidth={1}
            />
          ))}
          {!reduceMotion &&
            inBeams.map((beam, index) => (
              <motion.path
                key={`pulse-in-${index}`}
                d={beam.d}
                stroke="#3b82f6"
                strokeWidth={2}
                strokeLinecap="round"
                initial={{ pathLength: 0.18, pathOffset: 0, opacity: 0 }}
                animate={isInView ? { pathOffset: [0, 1], opacity: 1 } : {}}
                transition={{
                  pathOffset: {
                    duration: 3,
                    ease: "linear",
                    repeat: Infinity,
                    delay: index * 0.8,
                  },
                  opacity: { duration: 0.5, delay: index * 0.8 },
                }}
              />
            ))}
          {!reduceMotion &&
            outBeams.map((beam, index) => (
              <motion.path
                key={`pulse-out-${index}`}
                d={beam.d}
                stroke="#0ea5e9"
                strokeWidth={2.5}
                strokeLinecap="round"
                initial={{ pathLength: 0.18, pathOffset: 0, opacity: 0 }}
                animate={isInView ? { pathOffset: [0, 1], opacity: 1 } : {}}
                transition={{
                  pathOffset: {
                    duration: 3,
                    ease: "linear",
                    repeat: Infinity,
                    delay: 1.5 + index * 0.5,
                  },
                  opacity: { duration: 0.5, delay: 1.5 + index * 0.5 },
                }}
              />
            ))}
        </svg>
      )}

      <div className="relative z-10 grid grid-cols-3 gap-2 sm:flex sm:flex-col sm:gap-3">
        {sources.map((source, index) => (
          <motion.div
            key={source.name}
            ref={(el) => {
              sourceRefs.current[index] = el;
            }}
            initial={{ opacity: 0, x: -16 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.45, delay: index * 0.08 }}
            className="flex items-center gap-2 rounded-xl border border-white/70 bg-white/90 px-2.5 py-2 shadow-sm sm:px-3"
          >
            {source.icon && (
              <img
                src={source.icon}
                alt={source.name}
                width={22}
                height={22}
                className="h-[22px] w-[22px] shrink-0 object-contain"
              />
            )}
            <span className="truncate text-[11px] font-semibold text-[var(--blue-900)] sm:text-xs">
              {source.name}
            </span>
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center gap-3">
        <div ref={hubRef} className="relative">
          {!reduceMotion &&
            [0, 1.3].map((delay) => (
              <motion.span
                key={delay}
                className="absolute inset-0 rounded-3xl border-1 border-sky-400"
                initial={{ opacity: 0 }}
                animate={
                  isInView ? { scale: [1, 1.5], opacity: [0.2, 0] } : {}
                }
                transition={{
                  duration: 2.6,
                  ease: "easeOut",
                  repeat: Infinity,
                  delay,
                }}
              />
            ))}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative flex h-20 w-20 items-center justify-center rounded-[1.75rem] border border-white/70 bg-white shadow-sm sm:h-24 sm:w-24"
          >
            <img
              src="/icon/logo.png"
              alt={hubLabel}
              width={52}
              height={52}
              className="h-12 w-12 object-contain sm:h-14 sm:w-14"
            />
          </motion.div>
        </div>
        <motion.span
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-sm font-bold text-[var(--blue-900)]"
        >
          {hubLabel}
        </motion.span>
      </div>

      <div className="relative z-10 flex flex-row gap-4 sm:flex-col sm:gap-6">
        {tools.map((tool, index) => (
          <motion.div
            key={tool.name}
            initial={{ opacity: 0, x: 16 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.3 + index * 0.12 }}
            className="flex flex-col items-center gap-1.5"
          >
            <div
              ref={(el) => {
                toolRefs.current[index] = el;
              }}
              className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/70 bg-white shadow-md"
            >
              {tool.icon ? (
                <img
                  src={tool.icon}
                  alt={tool.name}
                  width={32}
                  height={32}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <span className="text-lg font-black text-[var(--blue-900)]">
                  {tool.name.charAt(0)}
                </span>
              )}
            </div>
            <span className="text-[11px] font-semibold text-[var(--blue-900)] sm:text-xs">
              {tool.name}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
